import { useCallback, useRef, type MouseEvent } from 'react';

import Text from '@cartwhl/ui/base/Text';
import FileIcon from '@cartwhl/ui/icons/FileIcon';
import FilesList, {
  type FilesListFile,
  type FilesListHandle,
} from '@cartwhl/ui/input/FilesList';

import { syncMediaUploadThumbnailJobs } from '../../shared/mediaUpload/mediaUploadThumbnailCache';
import { useMediaLibraryPicker } from '../../../hooks/useMediaLibraryPicker';
import BatchReferenceEmptyDropzone from '../../shared/BatchReferenceEmptyDropzone';
import { useStore } from '../batchComicUploadStore';
import ComicVideoListPanel from './ComicVideoListPanel';

const ALLOWED_MIME_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/mov',
  'video/webm',
];

/** Fixed empty dropzone height. Filled list scroll height is set on `ComicVideoListPanel`. */
const FILE_LIST_HEIGHT_PX = 200;

export default function FileUploadSection() {
  const store = useStore();
  const filesListRef = useRef<FilesListHandle>(null);
  const { fileIds, libraryVideos } = store.state;
  const hasVideos = fileIds.length + libraryVideos.length > 0;

  const handleFilesChange = useCallback(
    (files: FilesListFile[]) => {
      syncMediaUploadThumbnailJobs(files);
      store.syncFiles(files);
    },
    [store],
  );

  const getFilesListRef = useCallback(() => filesListRef.current, []);
  store.setFilesListRef(getFilesListRef);

  const getVideoInitialSelection = useCallback(
    () => libraryVideos,
    [libraryVideos],
  );

  const { openPicker, modal } = useMediaLibraryPicker({
    mediaKind: 'video',
    mode: 'multi',
    getInitialSelection: getVideoInitialSelection,
    onConfirm: (selections) => {
      store.setLibraryVideos(selections);
    },
  });

  const handleOpenLibraryPicker = useCallback(
    (event?: MouseEvent) => {
      event?.stopPropagation();
      openPicker();
    },
    [openPicker],
  );

  const handleOpenComputerPicker = useCallback(() => {
    filesListRef.current?.openFilePicker();
  }, []);

  const renderVideoList = useCallback(
    (
      localFiles: FilesListFile[],
      removeLocalFile: (fileId: string) => void,
    ) => (
      <ComicVideoListPanel
        libraryVideos={libraryVideos}
        localFiles={localFiles}
        onRemoveLibraryVideo={(mediaID) => store.removeLibraryVideo(mediaID)}
        onRemoveLocalFile={removeLocalFile}
        onAddFromLibrary={() => handleOpenLibraryPicker()}
        onAddFromComputer={handleOpenComputerPicker}
      />
    ),
    [
      handleOpenComputerPicker,
      handleOpenLibraryPicker,
      libraryVideos,
      store,
    ],
  );

  return (
    <div className="flex flex-col gap-1.5">
      <FilesList
        ref={filesListRef}
        allowedMimeTypes={ALLOWED_MIME_TYPES}
        onFilesChange={handleFilesChange}
        hideBuiltInAddMore
        height={FILE_LIST_HEIGHT_PX}
        emptyStateVariant={hasVideos ? 'plain' : 'dropzone'}
        emptyStateContent={
          hasVideos ? (
            renderVideoList([], () => {})
          ) : (
            <BatchReferenceEmptyDropzone
              onOpenLibrary={() => handleOpenLibraryPicker()}
              dropHint={
                <div className="flex items-center gap-1">
                  <FileIcon className="text-blue" />
                  <Text variant="studio/body/medium/sm">
                    <span className="text-blue">Select video file(s)</span>{' '}
                    <span className="text-gray-60">or drop them here</span>
                  </Text>
                </div>
              }
            />
          )
        }
        renderFileList={(files, removeFile) =>
          renderVideoList(files, (fileId) => removeFile(fileId))
        }
        renderItem={() => null}
        dragOverlayContent={
          <Text variant="studio/title/xl" className="text-white">
            Drop videos to upload
          </Text>
        }
      />

      {modal}

      <Text
        as="div"
        variant="studio/body/medium/sm"
        className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 px-2"
      >
        <a
          href="https://docs.getcartwheel.com/docs/integrating-cartwheel/integration"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue hover:underline"
        >
          Upload guidelines
        </a>
        <span className="text-tertiary">.mp4, .mov or .zip accepted</span>
      </Text>
    </div>
  );
}
